import { motion } from 'framer-motion'
import { t } from 'i18next'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useEtracker } from '../../../hooks/use-etracker'
import { Button } from '../../button/button'
import { RadioButtonGroup } from '../../radio-button-group/radio-button-group'

import { TextArea } from '../../text-area/text-area'
import styles from '../exit-feedback-modal.module.css'

interface JobDetailsExitFormProps {
	onSubmit: () => void
	exitButtonSlot?: React.ReactNode
}

export const JobDetailsExitForm: React.FC<JobDetailsExitFormProps> = ({ onSubmit, exitButtonSlot }) => {
	const {
		register,
		handleSubmit,
		watch,
		control,
		formState: { errors },
	} = useForm()
	const { trackExitEvent } = useEtracker()

	const roleSuitableValue = watch('role-suitable')

	// The form will have two separate questions that each fire their own event
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleFormSubmit = (data: any) => {
		const roleSuitableAnswer =
			roleSuitableOptions.find((option) => option.value === data['role-suitable'])?.label || ''
		const roleSuitableComment = data[`role-suitable-comment-${roleSuitableValue}`] || ''

		const roleSuitableResponse = `${roleSuitableAnswer}${roleSuitableComment ? `:${roleSuitableComment}` : ''}`

		trackExitEvent(t('exitFeedback.formJobDetails.roleSuitable.label'), roleSuitableResponse)

		const infoHelpfulAnswer = infoHelpfulOptions.find((option) => option.value === data['info-helpful'])?.label

		if (infoHelpfulAnswer) {
			trackExitEvent(t('exitFeedback.formJobDetails.infoHelpful.label'), infoHelpfulAnswer)
		}

		if (onSubmit) {
			onSubmit()
		}
	}

	const roleSuitableOptions = [
		{
			value: 'yes',
			label: t('exitFeedback.formJobDetails.roleSuitable.options.yes'),
			append: (
				<motion.div
					animate={{
						opacity: roleSuitableValue === 'yes' ? 1 : 0,
						height: roleSuitableValue === 'yes' ? 'auto' : '0px',
					}}
					transition={{ duration: 0.3, ease: 'easeInOut' }}
					style={{ overflow: 'hidden' }}
				>
					<TextArea
						control={control}
						name={'role-suitable-comment-yes'}
						placeholder={t('exitFeedback.formJobDetails.roleSuitable.commentPlaceholder')}
						disabled={roleSuitableValue !== 'yes'}
					/>
				</motion.div>
			),
		},
		{
			value: 'no',
			label: t('exitFeedback.formJobDetails.roleSuitable.options.no'),
			append: (
				<motion.div
					animate={{
						opacity: roleSuitableValue === 'no' ? 1 : 0,
						height: roleSuitableValue === 'no' ? 'auto' : '0px',
					}}
					transition={{ duration: 0.3, ease: 'easeInOut' }}
					style={{ overflow: 'hidden' }}
				>
					<TextArea
						control={control}
						name={'role-suitable-comment-no'}
						placeholder={t('exitFeedback.formJobDetails.roleSuitable.commentPlaceholder')}
						disabled={roleSuitableValue !== 'no'}
					/>
				</motion.div>
			),
		},
	]

	const infoHelpfulOptions = [
		{
			value: 'yes',
			label: t('exitFeedback.formJobDetails.infoHelpful.options.yes'),
		},
		{
			value: 'no',
			label: t('exitFeedback.formJobDetails.infoHelpful.options.no'),
		},
	]

	return (
		<form className={styles.form} onSubmit={handleSubmit(handleFormSubmit)}>
			<div className={styles.fields}>
				<RadioButtonGroup
					name="role-suitable"
					label={t('exitFeedback.formJobDetails.roleSuitable.label')}
					options={roleSuitableOptions}
					className={styles.formGroup}
					register={register}
					registerOptions={{ required: t('exitFeedback.fieldRequired') }}
					error={errors['role-suitable'] && t('exitFeedback.fieldRequired')}
				/>

				<RadioButtonGroup
					name="info-helpful"
					label={t('exitFeedback.formJobDetails.infoHelpful.label')}
					options={infoHelpfulOptions}
					className={styles.formGroup}
					register={register}
					registerOptions={{ required: t('exitFeedback.fieldRequired') }}
					error={errors['info-helpful'] && t('exitFeedback.fieldRequired')}
				/>
			</div>

			<div className={styles.buttons}>
				{exitButtonSlot}
				<Button className={styles.submit} type="primary" submit={true}>
					{t('exitFeedback.submit')}
				</Button>
			</div>
		</form>
	)
}
